import {
  defaultTitlePattern,
  renderTitle,
  titleVars,
  CUSTOM_ROUTE_NAME,
  PRIORITIES,
  PRIORITY_LABELS,
  SUBJECT_FIELD,
  TASK_STATUS_LABELS,
  type RequestTypeDto,
  type Page,
  type Priority,
  type RouteDto,
  type TaskScope,
  type TaskSort,
  type TaskSummaryDto,
} from '@edms/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CUSTOM_ROUTE, CustomRouteEditor, customRouteProblem, emptyCustomRoute } from '../components/custom-route';
import { FieldsForm, type Values } from '../components/fields';
import { RichTextEditor } from '../components/rich-text';
import { TypeIcon } from '../components/type-icon';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { DOC_DUE_HINT, Due, fromLocalInput, PriorityMark, StatusBadge, useDepartmentsList, useInboxCounts, UserSelect, useRouteOptions, useUsersList } from '../lib/tasks-ui';
import { Empty, Pager, useDebounced } from '../lib/ui';

const LIMIT = 50;
/** Дата создания в списке — с годом: в списке есть и задачи прошлых лет. */
const createdFmt = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });

const TABS: Array<{ scope: TaskScope; label: string; hint: string }> = [
  { scope: 'inbox', label: 'Ждут меня', hint: 'Согласовать, выполнить или проверить' },
  { scope: 'mine', label: 'Я автор', hint: 'Документы и поручения, которые вы создали' },
  { scope: 'assigned', label: 'Мне поручено', hint: 'Поручения и подзадачи на вас' },
  { scope: 'involved', label: 'Все мои', hint: 'Где вы автор, исполнитель, согласующий или наблюдатель' },
  { scope: 'all', label: 'Все задачи', hint: 'Все задачи организации' },
];

/** wide — полный список: отдельные колонки «Автор» и «Создана» (на телефоне автор — под названием). */
export function TaskRow({ t, onOpen, wide }: { t: TaskSummaryDto; onOpen: () => void; wide?: boolean }) {
  const closed = ['approved', 'done', 'rejected', 'cancelled'].includes(t.status);
  return (
    <tr className="clickable" onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <td className="faint num">№{t.number}</td>
      <td>
        <div className="task-title">
          <Icon name={t.kind === 'document' ? 'route' : 'tasks'} size={16} />
          <b>{t.root ? `Подзадача: ${t.root.title}` : t.title}</b>
          {t.requestTypeName && t.requestTypeName !== 'Документ' && <span className="badge">{t.requestTypeName}</span>}
          <PriorityMark priority={t.priority} />
          {t.pendingStepId && <span className="badge badge-info">ваше решение</span>}
        </div>
        {t.root && <div className="subtask-what">{t.title}</div>}
        <div className="faint">
          <span className={wide ? 'only-mobile' : undefined}>{t.author.fullName}</span>
          {t.assignee ? `${wide ? '' : ' '}→ ${t.assignee.fullName}` : ''}
        </div>
      </td>
      {wide && <td className="hide-mobile">{t.author.fullName}</td>}
      <td>
        <StatusBadge status={t.status} />
      </td>
      <td className="hide-mobile">
        <RowDue t={t} closed={closed} />
      </td>
      {wide && <td className="hide-mobile faint nowrap">{createdFmt.format(new Date(t.createdAt))}</td>}
    </tr>
  );
}

/**
 * Ждёт решения пользователя — срок его шага из маршрута. У документа общий срок —
 * только ориентир «нужен к», напоминаний по нему нет.
 */
function RowDue({ t, closed }: { t: TaskSummaryDto; closed: boolean }) {
  if (t.pendingStepId && t.pendingStepDueAt) return <Due at={t.pendingStepDueAt} />;
  if (t.kind === 'document') return t.dueAt ? <Due at={t.dueAt} done={closed} prefix="нужен к" /> : <span className="faint">—</span>;
  return <Due at={t.dueAt} done={closed} />;
}

/** inbox — очередь «Ждут меня»; иначе — вкладки со всеми задачами пользователя. */
export function TasksPage({ inbox = false, requestType }: { inbox?: boolean; requestType?: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const scope: TaskScope = inbox ? 'inbox' : ((params.get('scope') as TaskScope) || 'involved');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [author, setAuthor] = useState('');
  const [offset, setOffset] = useState(0);
  // По умолчанию: «Ждут меня» — ближайшие сроки сверху, остальные — сначала новые.
  const [order, setOrder] = useState<{ sort: TaskSort; dir: 'asc' | 'desc' }>(inbox ? { sort: 'due', dir: 'asc' } : { sort: 'created', dir: 'desc' });
  const q = useDebounced(search);
  const authorQ = useDebounced(author);
  const counts = useInboxCounts();
  const users = useUsersList();
  useEffect(() => setOffset(0), [q, status, authorQ, scope, order]);

  const tasks = useQuery({
    queryKey: ['tasks', 'list', { scope, q, status, authorQ, offset, requestType, order }],
    queryFn: () => api.get<Page<TaskSummaryDto>>('/tasks', { scope, search: q, status, author: authorQ, requestType, ...order, limit: LIMIT, offset }),
    placeholderData: (p) => p,
  });
  // Повторное нажатие на колонку меняет направление; новая колонка — сначала «новые / Я–А» для дат, «А–Я» для остального.
  const sortBy = (sort: TaskSort) =>
    setOrder((o) => (o.sort === sort ? { sort, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { sort, dir: sort === 'created' || sort === 'number' ? 'desc' : 'asc' }));
  const th = (sort: TaskSort, label: string, props: { className?: string; style?: CSSProperties } = {}) => (
    <th {...props} aria-sort={order.sort === sort ? (order.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`th-sort${order.sort === sort ? ' active' : ''}`} onClick={() => sortBy(sort)} title="Сортировать">
        {label}
        <span aria-hidden>{order.sort === sort ? (order.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
  const tabs = TABS.filter((t) => t.scope !== 'inbox' && (t.scope !== 'all' || can('requests.view_all')));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{inbox ? 'Ждут меня' : requestType === 'trip' ? 'Командировки' : 'Задачи'}</h1>
          <p className="muted">{TABS.find((t) => t.scope === scope)?.hint}</p>
        </div>
        {requestType === 'trip' ? (
          <Link to="/tasks/new?kind=document&type=trip" className="btn btn-primary">
            <Icon name="plus" size={18} /> Новая командировка
          </Link>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/tasks/new?kind=assignment" className="btn">
              <Icon name="plus" size={18} /> Поручение
            </Link>
            <Link to="/tasks/new" className="btn btn-primary">
              <Icon name="plus" size={18} /> Документ или заявка
            </Link>
          </div>
        )}
      </div>

      {inbox && counts.data && (
        <div className="inbox-stats">
          <span className="chip">На согласование: {counts.data.approvals}</span>
          <span className="chip">Выполнить: {counts.data.assignments}</span>
          <span className="chip">Проверить: {counts.data.reviews}</span>
        </div>
      )}
      {!inbox && (
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.scope}
            role="tab"
            aria-selected={scope === t.scope}
            className={`tab${scope === t.scope ? ' active' : ''}`}
            onClick={() => setParams({ scope: t.scope })}
          >
            {t.label}
          </button>
        ))}
      </div>
      )}

      <div className="card">
        <div className="toolbar">
          <input className="input" type="search" placeholder="Название или №" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Поиск" />
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Статус">
            <option value="">Любой статус</option>
            {Object.entries(TASK_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <input className="input" type="search" list="task-authors" placeholder="Автор" value={author} onChange={(e) => setAuthor(e.target.value)} aria-label="Автор" />
          <datalist id="task-authors">
            {users.data?.map((u) => <option key={u.id} value={u.fullName} />)}
          </datalist>
          {(search || status || author) && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setSearch('');
                setStatus('');
                setAuthor('');
              }}
            >
              Сбросить
            </button>
          )}
        </div>
        {tasks.data?.items.length === 0 ? (
          scope === 'inbox' ? (
            <Empty emoji="☕" title="Ничего не ждёт вашего решения">Когда кто-то отправит вам документ или поручение, он появится здесь</Empty>
          ) : (
            <Empty emoji="🗂" title="Задач нет" />
          )
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {th('number', '№', { style: { width: 70 } })}
                  {th('title', 'Задача')}
                  {th('author', 'Автор', { className: 'hide-mobile' })}
                  {th('status', 'Статус')}
                  {th('due', 'Срок', { className: 'hide-mobile' })}
                  {th('created', 'Создана', { className: 'hide-mobile' })}
                </tr>
              </thead>
              <tbody>
                {tasks.data?.items.map((t) => (
                  <TaskRow key={t.id} t={t} wide onOpen={() => navigate(`/tasks/${t.id}`)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tasks.data && <Pager total={tasks.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>
    </>
  );
}

/** Создание: сначала вид (документ, командировка, отпуск, свои типы, поручение), затем форма. */
export function NewTaskPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const types = useQuery({ queryKey: ['request-types'], queryFn: () => api.get<RequestTypeDto[]>('/request-types') });
  const kind = params.get('kind');
  const typeKey = params.get('type');

  if (kind === 'assignment') return <NewTaskForm kind="assignment" type={null} />;
  const type = types.data?.find((t) => t.key === typeKey) ?? null;
  if (type) return <NewTaskForm kind="document" type={type} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Что создаём?</h1>
          <p className="muted">Поля и маршрут согласования подставятся по выбранному виду</p>
        </div>
      </div>
      <div className="type-cards">
        {types.data?.map((t) => (
          <button key={t.id} className="card type-card" onClick={() => setParams({ kind: 'document', type: t.key })}>
            <TypeIcon icon={t.icon} />
            <b>{t.name}</b>
            {t.description && <span className="faint">{t.description}</span>}
          </button>
        ))}
        <button className="card type-card" onClick={() => setParams({ kind: 'assignment' })}>
          <span className="type-icon" aria-hidden>
            ✅
          </span>
          <b>Поручение</b>
          <span className="faint">Задача исполнителю без согласования: он отвечает, вы принимаете результат</span>
        </button>
        {can('regulations.manage') && (
          <Link className="card type-card" to="/regulations?new=1">
            <span className="type-icon" aria-hidden>
              🗓️
            </span>
            <b>Регулярные задачи</b>
            <span className="faint">Создаются сами по расписанию: каждую неделю, месяц, квартал. Исполнители выполняют, ответственный принимает</span>
          </Link>
        )}
      </div>
    </>
  );
}

function NewTaskForm({ kind, type }: { kind: 'document' | 'assignment'; type: RequestTypeDto | null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  // Пока автор не правил название сам, оно собирается из маршрута и полей (шаблон в типе заявки).
  const [titleEdited, setTitleEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [due, setDue] = useState('');
  const [routeId, setRouteId] = useState(type?.defaultRouteId ?? '');
  const [customRoute, setCustomRoute] = useState(emptyCustomRoute);
  const custom = routeId === CUSTOM_ROUTE;
  const [assigneeId, setAssigneeId] = useState('');
  const [fields, setFields] = useState<Values>(() => {
    const v: Values = {};
    if (type?.fields.some((f) => f.key === SUBJECT_FIELD)) v[SUBJECT_FIELD] = user?.id;
    if (type?.fields.some((f) => f.key === 'position') && user?.position) v.position = user.position;
    return v;
  });
  const routeOptions = useRouteOptions(type?.id ?? null, kind === 'document');
  // Свой маршрут выбирается, даже если маршрут из справочника закреплён за типом.
  const routeLocked = type !== null && !type.allowRouteChoice && Boolean(type.defaultRouteId) && !type.allowCustomRoute;
  const users = useUsersList();
  const departments = useDepartmentsList();
  const autoTitle =
    kind === 'document'
      ? renderTitle(
          type?.titleTemplate || defaultTitlePattern(type?.fields ?? []),
          titleVars(
            type?.fields ?? [],
            fields,
            { route: custom ? CUSTOM_ROUTE_NAME : routeOptions.find((r) => r.id === routeId)?.name, type: type?.name, author: user?.fullName, today: new Date().toLocaleDateString('ru-RU') },
            { user: (id) => users.data?.find((u) => u.id === id)?.fullName, department: (id) => departments.data?.find((d) => d.id === id)?.name },
          ),
        )
      : '';
  useEffect(() => {
    if (!titleEdited) setTitle(autoTitle);
  }, [autoTitle, titleEdited]);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/tasks', {
        kind,
        title,
        description: description || undefined,
        priority,
        dueAt: fromLocalInput(due),
        ...(kind === 'document'
          ? { routeId: custom ? null : routeId || null, ...(custom ? { customRoute } : {}), requestTypeId: type?.id ?? null, fields }
          : { assigneeId }),
      }),
    onSuccess: (r) => navigate(`/tasks/${r.id}`, { replace: true }),
  });
  const err = create.error instanceof ApiError ? create.error : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (custom && customRouteProblem(customRoute)) return;
    create.mutate();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/tasks/new" className="faint">
            ← другой вид
          </Link>
          <h1>{kind === 'assignment' ? 'Новое поручение' : type?.name}</h1>
          <p className="muted">
            {kind === 'document'
              ? 'Сохраняется черновик: на следующем шаге вы увидите, кто и в каком порядке будет согласовывать. Обязательные поля (*) нужны к запуску'
              : 'Исполнитель получит уведомление и письмо, а вы — его ответ на проверку'}
          </p>
        </div>
      </div>
      <form className="card card-pad form-grid wide-form" onSubmit={submit}>
        {err && !Object.keys(err.fields).some((k) => k.startsWith('fields.')) && <div className="alert">{err.message}</div>}
        <label className="field">
          <span>Название</span>
          <input
            className="input"
            required={kind === 'assignment'}
            autoFocus={kind === 'assignment'}
            maxLength={500}
            value={title}
            placeholder={kind === 'document' ? 'Заполнится само по маршруту и полям' : undefined}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleEdited(e.target.value.trim() !== '');
            }}
            aria-invalid={Boolean(err?.fields.title)}
          />
          {kind === 'document' && (
            <span className="faint">
              {titleEdited ? (
                <>
                  Название изменено вручную ·{' '}
                  <button type="button" className="link-btn" onClick={() => setTitleEdited(false)}>
                    вернуть автоматическое
                  </button>
                </>
              ) : (
                'Собирается автоматически — можно исправить'
              )}
            </span>
          )}
          {err?.fields.title && <span className="field-error">{err.fields.title}</span>}
        </label>
        {type && type.fields.length > 0 && (
          <FieldsForm fields={type.fields.filter((f) => !f.byApprover)} values={fields} onChange={setFields} errors={err?.fields} behavior={type.behavior} calc={type.calc} requestTypeId={type.id} />
        )}
        <div className="field">
          <span>{type && type.fields.length > 0 ? 'Комментарий' : 'Описание'}</span>
          <RichTextEditor value={description} onChange={setDescription} placeholder="Можно выделить жирным, добавить цитату, список или ссылку" />
        </div>
        {kind === 'document' ? (
          <>
            <label className="field">
              <span>Маршрут согласования</span>
              <select className="select" value={routeId} onChange={(e) => setRouteId(e.target.value)} disabled={routeLocked} aria-invalid={Boolean(err?.fields.routeId)}>
                <option value="">Выберите маршрут</option>
                {routeOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
                {type?.allowCustomRoute && <option value={CUSTOM_ROUTE}>Свой маршрут — выбрать участников самому</option>}
              </select>
              {routeLocked && <span className="faint">Маршрут задан для этого вида заявки</span>}
              {err?.fields.routeId && <span className="field-error">{err.fields.routeId}</span>}
            </label>
            {custom && user && (
              <div className="field">
                <span>Свой маршрут — только для этого документа</span>
                <CustomRouteEditor value={customRoute} onChange={setCustomRoute} authorId={user.id} error={err?.fields.customRoute} />
              </div>
            )}
          </>
        ) : (
          <label className="field">
            <span>Исполнитель</span>
            <UserSelect value={assigneeId} onChange={setAssigneeId} invalid={Boolean(err?.fields.assigneeId)} />
            {err?.fields.assigneeId && <span className="field-error">{err.fields.assigneeId}</span>}
          </label>
        )}
        <div className="two-cols">
          <label className="field">
            <span>Приоритет</span>
            <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{kind === 'document' ? 'Нужен к (необязательно)' : 'Срок'}</span>
            <input className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
            {kind === 'document' && <span className="faint">{DOC_DUE_HINT}</span>}
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" disabled={create.isPending || (kind === 'assignment' && (!title.trim() || !assigneeId))}>
            {kind === 'document' ? 'Сохранить черновик →' : 'Поручить'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Отмена
          </button>
        </div>
      </form>
    </>
  );
}
